import { apiFetch } from '@/lib/api';

export interface FeedbackTicket {
  id: string;
  organization_id: string;
  user_id: string;
  type: 'bug' | 'feature' | 'help';
  subject: string | null;
  body: string | null;
  status: 'open' | 'in_progress' | 'resolved' | 'closed';
  metadata: Record<string, any>;
  created_at: string;
  updated_at: string;
}

export interface FeedbackMessage {
  id: string;
  ticket_id: string;
  user_id: string | null;
  role: 'user' | 'admin' | 'ai';
  body: string;
  attachments: Attachment[];
  created_at: string;
}

export interface Attachment {
  filename: string;
  r2_key: string;
  url: string;
  mime_type: string;
  size: number;
}

export interface TicketDetail {
  ticket: FeedbackTicket & { user_email: string; user_name: string | null; org_name: string | null };
  messages: FeedbackMessage[];
}

export interface HelpChatResponse {
  response: string;
  escalated: boolean;
}

export const feedbackService = {
  async createTicket(data: {
    type: 'bug' | 'feature' | 'help';
    subject?: string;
    body?: string;
    metadata?: Record<string, any>;
    attachments?: Attachment[];
  }): Promise<FeedbackTicket> {
    const res = await apiFetch('/api/feedback/tickets', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    });
    const json = await res.json();
    if (!res.ok) throw new Error(json.error || 'Failed to create ticket');
    return json.data;
  },

  async listTickets(filters?: { type?: string; status?: string; page?: number }): Promise<{ data: FeedbackTicket[]; total: number }> {
    const params = new URLSearchParams();
    if (filters?.type) params.set('type', filters.type);
    if (filters?.status) params.set('status', filters.status);
    if (filters?.page) params.set('page', String(filters.page));
    const res = await apiFetch(`/api/feedback/tickets?${params}`);
    const json = await res.json();
    if (!res.ok) throw new Error(json.error || 'Failed to list tickets');
    return json;
  },

  async getTicket(id: string): Promise<TicketDetail> {
    const res = await apiFetch(`/api/feedback/tickets/${id}`);
    const json = await res.json();
    if (!res.ok) throw new Error(json.error || 'Failed to get ticket');
    return json.data;
  },

  async sendMessage(ticketId: string, body: string, attachments?: Attachment[]): Promise<HelpChatResponse | { message: FeedbackMessage }> {
    const res = await apiFetch(`/api/feedback/tickets/${ticketId}/messages`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ body, attachments }),
    });
    const json = await res.json();
    if (!res.ok) throw new Error(json.error || 'Failed to send message');
    return json.data;
  },

  async presignAttachment(ticketId: string, filename: string, contentType: string): Promise<{ url: string; r2Key: string; publicUrl: string }> {
    const res = await apiFetch('/api/feedback/presign', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ ticketId, filename, contentType }),
    });
    const json = await res.json();
    if (!res.ok) throw new Error(json.error || 'Failed to presign');
    return json.data;
  },

  async uploadAttachment(ticketId: string, file: File): Promise<Attachment> {
    // 1. Get presigned URL
    const { url, r2Key, publicUrl } = await feedbackService.presignAttachment(ticketId, file.name, file.type);
    // 2. Upload to R2
    await fetch(url, {
      method: 'PUT',
      body: file,
      headers: { 'Content-Type': file.type },
    });
    // 3. Return attachment info
    return {
      filename: file.name,
      r2_key: r2Key,
      url: publicUrl,
      mime_type: file.type,
      size: file.size,
    };
  },
};
